import { hashAnswerValue } from './hash.js';
import type {
  ActorContext,
  ContextProvenanceManifest,
  PromptPurpose,
  ProviderKind,
  ValidationStatus,
  WorldContext,
} from './types.js';
import { PROMPT_SCHEMA_VERSION } from './types.js';

export interface ManifestInput {
  purpose: PromptPurpose;
  world: WorldContext;
  actorIds: string[];
  actors: ActorContext[];
  deviceIds: string[];
  eventIds: string[];
  summaryIds: string[];
  provider: ProviderKind | 'local';
  fallbackUsed: boolean;
  validationStatus: ValidationStatus;
  contextTierMax: number;
  contextTierUsed: number;
  children?: ContextProvenanceManifest[];
}

export function buildProvenanceManifest(input: ManifestInput): ContextProvenanceManifest {
  const answerIds = Object.keys(input.world.answers).sort();
  const answerHashes: Record<string, string> = {};
  for (const id of answerIds) {
    const answer = input.world.answers[id];
    if (answer) answerHashes[id] = hashAnswerValue(id, answer.value);
  }

  const actorVersions: Record<string, number> = {};
  for (const actor of input.actors) {
    if (input.actorIds.includes(actor.actorId)) {
      actorVersions[actor.actorId] = actor.characterVersion;
    }
  }

  return {
    purpose: input.purpose,
    schemaVersion: PROMPT_SCHEMA_VERSION,
    worldId: input.world.worldId,
    worldVersion: input.world.worldVersion,
    answerIds,
    answerHashes,
    actorIds: [...input.actorIds].sort(),
    actorVersions,
    deviceIds: [...input.deviceIds].sort(),
    eventIds: [...input.eventIds].sort(),
    summaryIds: [...input.summaryIds].sort(),
    provider: input.provider,
    fallbackUsed: input.fallbackUsed,
    validationStatus: input.validationStatus,
    contextTierMax: input.contextTierMax as ContextProvenanceManifest['contextTierMax'],
    contextTierUsed: input.contextTierUsed as ContextProvenanceManifest['contextTierUsed'],
    ...(input.children?.length ? { children: input.children } : {}),
  };
}

/** Test/helper — manifest must never carry raw secrets, keys, or private answer payloads. */
export function findManifestRedactionViolations(
  manifest: ContextProvenanceManifest,
  forbiddenSamples: string[],
): string[] {
  const json = JSON.stringify(manifest);
  return forbiddenSamples.filter((sample) => sample.length > 0 && json.includes(sample));
}

export function mergeManifests(
  parent: ContextProvenanceManifest,
  child: ContextProvenanceManifest,
): ContextProvenanceManifest {
  return {
    ...parent,
    children: [...(parent.children ?? []), child],
    validationStatus:
      parent.validationStatus === 'valid' && child.validationStatus === 'valid'
        ? 'valid'
        : child.validationStatus === 'fallback' || parent.validationStatus === 'fallback'
          ? 'fallback'
          : 'invalid',
  };
}
